import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IGroupedTaxReturnsToCreate } from '../../data-types/IGroupedTaxReturnsToCreate';
import { ITaxReturnToCreate } from '../../data-types/ITaxReturnToCreate';
import AddReturnCard from './AddReturnCard';
import { ShowableTaxReturnItemRecord } from './AddReturnPanel';

export interface IAddReturnCardGroupProps {
    groupedTaxReturnToCreate: IGroupedTaxReturnsToCreate;
    index: number;
    duplicatedReturns: string[];
    onRemoveClicked: (entityId: string, calculationId: string) => void;
    onEditReturn: (taxReturn: ShowableTaxReturnItemRecord) => void;
}
const useStyles = makeStyles({
    flexContainerStyles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flexWrap: 'nowrap',
        width: 'auto',
        height: 'auto',
        '> *': {
            textOverflow: 'ellipsis',
            flexShrink: 1,
        },
        '> :not(:first-child)': {
            marginTop: '0px',
        },
        ':focus': {
            border: '1px solid  black ',
        },
    },
    headerTextStyles: {
        fontSize: '12px',
        lineHeight: '16px',
        fontWeight: 600,
    },
    containerStyles: {
        padding: '12px',
        backgroundColor: tokens.colorNeutralBackground3,
    },
});

const AddReturnCardGroup: React.FC<IAddReturnCardGroupProps> = ({
    groupedTaxReturnToCreate,
    index,
    onRemoveClicked,
    onEditReturn,
    duplicatedReturns,
}) => {
    const { t } = useTranslation();
    const { flexContainerStyles, headerTextStyles, containerStyles } = useStyles();
    const cardRefs = useRef<HTMLDivElement[]>([]);
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const focusedIndex = cardRefs.current?.findIndex((ref: HTMLDivElement) => ref?.contains(document.activeElement));

        if (event.key === 'ArrowDown') {
            const nextIndex = (focusedIndex + 1) % groupedTaxReturnToCreate.taxReturnsToCreate.length;
            cardRefs.current[nextIndex]?.focus();
            event.preventDefault();
        }

        if (event.key === 'ArrowUp') {
            const prevIndex =
                (focusedIndex - 1 + groupedTaxReturnToCreate.taxReturnsToCreate.length) %
                groupedTaxReturnToCreate.taxReturnsToCreate.length;
            cardRefs.current[prevIndex]?.focus();
            event.preventDefault();
        }
    };

    const handleEditButtonClick = (taxReturn: ITaxReturnToCreate) => {
        const mappedObject: ShowableTaxReturnItemRecord = {
            taxReturnItemName: taxReturn.taxReturnItemName,
            taxReturnItemDescription: taxReturn.taxReturnItemDescription,
            jurisdictionCode: taxReturn.jurisdictionCode,
            taxYearName: taxReturn.taxYearName,
            entityKey: groupedTaxReturnToCreate.entityKey,
            entityCode: groupedTaxReturnToCreate.entityCode,
            entityLabel: groupedTaxReturnToCreate.entityName
                ? `${groupedTaxReturnToCreate.entityCode} - ${groupedTaxReturnToCreate.entityName}`
                : groupedTaxReturnToCreate.entityCode,
            calculationKey: taxReturn.calculation.calculationKey,
            calculationName: taxReturn.calculation.name,
            calculationDescription: taxReturn.calculation.description,
            caseCode: taxReturn.caseCode,
            taxReturnItemPeriodKey: taxReturn.periodKey,
            taxReturnItemCaseKey: taxReturn.caseKey,
            columnFileKey: taxReturn.columnFileKey,
            rangeFileKey: taxReturn.rangeFileKey,
            includeAllBU: taxReturn.includeAllBU,
            ignoreEntityGroup: taxReturn.ignoreEntityGroup,
            updateColumnFileKey: taxReturn.updateColumnFileKey,
            updateBack: taxReturn.updateBack,
            updateBackReport: taxReturn.updateBackReport,
            alwaysRefresh: taxReturn.alwaysRefresh,
            defaultRangeName: groupedTaxReturnToCreate.rangeName,
        };

        onEditReturn(mappedObject);
    };

    return (
        <div
            data-testid={`entity-card-${index}`}
            id={`entity-card-${index}`}
            aria-label={`${groupedTaxReturnToCreate.entityName} ${groupedTaxReturnToCreate.taxReturnsToCreate.length} ${t('items', {
                count: groupedTaxReturnToCreate.taxReturnsToCreate.length,
            })}`}
            className={mergeClasses(flexContainerStyles, containerStyles)}
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            <h3 className={headerTextStyles}>
                {groupedTaxReturnToCreate.entityName} ({groupedTaxReturnToCreate.taxReturnsToCreate.length}{' '}
                {t('items', { count: groupedTaxReturnToCreate.taxReturnsToCreate.length })})
            </h3>
            {groupedTaxReturnToCreate.taxReturnsToCreate.map((taxReturn, cardIndex) => (
                <AddReturnCard
                    ref={(el: HTMLDivElement) => (cardRefs.current[cardIndex] = el)}
                    key={taxReturn.taxReturnItemName}
                    isDuplicated={duplicatedReturns.includes(taxReturn.taxReturnItemName)}
                    taxReturnItemName={taxReturn.taxReturnItemName}
                    calculationKey={taxReturn.calculation.calculationKey ?? 0}
                    calculationName={taxReturn.calculation.name ?? ''}
                    calculationDescription={taxReturn.calculation.description ?? ''}
                    jurisdictionCode={taxReturn.jurisdictionCode}
                    entityKey={groupedTaxReturnToCreate.entityKey}
                    caseCode={taxReturn.caseCode}
                    taxYearName={taxReturn.taxYearName}
                    onDeleteReturn={() => {
                        onRemoveClicked(groupedTaxReturnToCreate.entityId, taxReturn.calculation.id ?? '');
                        document.getElementById(`entity-card-${index}`)?.focus();
                    }}
                    onEditReturn={() => handleEditButtonClick(taxReturn)}
                />
            ))}
        </div>
    );
};

export default AddReturnCardGroup;
